<script setup lang="ts">
import BatchNumber from '@/components/features/batches/form-elements/BatchNumberInput.vue'
import { computed, onMounted, reactive, ref, watch } from 'vue'
import BatchQuality from '@/components/features/batches/form-elements/BatchQualityDropdown.vue'
import MergeDropdown from '@/components/features/batches/form-elements/MergeDropdown.vue'
import MergeList from '@/components/features/batches/form-elements/MergeList.vue'
import type { MergeUsage } from '@/types/batch-types'
import type { Supplier } from '@/types/supplier-types'
import RollsList from '@/components/features/batches/form-elements/RollsList.vue'
import DateKnittedInput from '@/components/features/batches/form-elements/DateKnittedInput.vue'
import SupplierInput from '@/components/features/batches/form-elements/SupplierInput.vue'
import WeightInput from '@/components/features/batches/form-elements/WeightInput.vue'
import MachineNumberInput from '@/components/features/batches/form-elements/MachineNumberInput.vue'
import BayInput from '@/components/features/batches/form-elements/BayInput.vue'
import PurchaseOrderReferenceInput from '@/components/features/batches/form-elements/PurchaseOrderReferenceInput.vue'
import NotesInput from '@/components/features/batches/form-elements/NotesInput.vue'
import type { Product } from '@/types/product-types'
import { getYarnsList } from '@/api/yarns'
import type { Yarn } from '@/types/yarn-types'
import type { Roll } from '@/types/roll-types'

const selectedMerges = ref<string[]>([])

const props = defineProps<{
  values: any
  type: 'add' | 'edit' | 'view'
  errors: any
}>()

const form = reactive({
  batchNo: '',
  knittingQuality: undefined as Product | undefined,
  mergeUsage: [] as MergeUsage[],
  knittingSupplier: undefined as Supplier | undefined,
  dateKnitted: '',
  weight: undefined as number | undefined,
  machineNumber: '',
  bay: '',
  purchaseOrderReference: '',
  notes: '',
  rolls: [] as { weight: number }[]
})

// const mergeUsage = computed(() => selectedMerges.value.map(mergeNo => ({
//   mergeId: form.batchQuality?.merges.find(merge => merge.mergeNumber === mergeNo)?.id,
//   mergeNumber: mergeNo,
//   kiloUsed: 0,
//   kiloWasted: 0
// } as MergeUsage)))

const updatedRolls = (newRolls: Roll[]) => {
  form.rolls = newRolls
}

const mergeUsage = computed(() =>
  selectedMerges.value.map((mergeNo) => {
    const merge = form.knittingQuality?.merges?.find((m) => m.mergeNumber === mergeNo)
    console.log('MergeNo:', mergeNo, 'Found Merge:', merge) // Debugging
    return {
      mergeId: merge ? merge.id : null,
      mergeNumber: mergeNo,
      kiloUsed: 0,
      kiloWasted: 0
    } as MergeUsage
  })
)

const totalWeight = computed(() => {
  if (!form.rolls || form.rolls.length === 0) return 0
  return form.rolls.reduce((acc, roll) => acc + roll.weight, 0)
})

const allMerges = ref<Yarn[]>([])

onMounted(async () => {
  allMerges.value = await getYarnsList()
})

// Watch for changes in selectedMerges and update form.mergeUsage
// watch(mergeUsage, (newMerges) => {
//   // Remove merges that are no longer selected
//   form.mergeUsage = form.mergeUsage.filter(existingMerge =>
//     newMerges.some(newMerge => newMerge.mergeNumber === existingMerge.mergeNumber)
//   )
//
//   // Add new merges
//   newMerges.forEach(newMerge => {
//     const existingMerge = form.mergeUsage.find(
//       existing => existing.mergeNumber === newMerge.mergeNumber
//     )
//
//     if (!existingMerge) {
//       // If it's a new merge, push it into the form
//       form.mergeUsage.push(newMerge)
//     }
//   })
// })

watch(totalWeight, (newWeight) => {
  form.weight = newWeight

  updateMergeUsageWeights()
})

watch(selectedMerges, (newSelected) => {
  form.mergeUsage = newSelected.map(
    (mergeNo) =>
      ({
        mergeId:
          allMerges.value.find((merge) => String(merge.mergeNumber) === String(mergeNo))?.id || '',
        mergeNumber: mergeNo,
        kiloUsed: 0,
        kiloWasted: 0
      }) as MergeUsage
  )

  updateMergeUsageWeights()
})

const updateMergeUsageWeights = () => {
  if (!form.weight || !form.knittingQuality || !form.knittingQuality.yarnsUsed) return

  form.mergeUsage.forEach(mergeUsage => {
    const merge = allMerges.value.find(m => String(m.mergeNumber) === String(mergeUsage.mergeNumber))
    if (!merge) return

    const yarnComposition = form.knittingQuality?.yarnsUsed.find(yarn =>
        yarn.yarnType === merge.yarnType &&
        yarn.yarnCount.toString() === merge.yarnCount &&
        yarn.yarnColour === merge.yarnColour
    )

    if (yarnComposition && yarnComposition.percentageComposition) {
      mergeUsage.kiloUsed = Number((((form.weight as number) * yarnComposition.percentageComposition) / 100).toFixed(2))
    }
  })
}

watch(() => form.weight, (newWeight) => {
  if (newWeight) {
    updateMergeUsageWeights()
  }
})

watch(
  () => form.knittingQuality,
  (newKnittingQuality ) => {
    if (newKnittingQuality ) {
      form.rolls = Array(newKnittingQuality .standardBatchSize).fill({ weight: 0 })

      setTimeout(() => {
        updateMergeUsageWeights()
      }, 0)
    }
  }
)
</script>

<template>
  <div class="grid grid-cols-12 gap-20 gap-y-4">
    <BatchNumber v-model="form.batchNo" input-name="batchNo" class="col-span-6" />
    <BatchQuality v-model="form.knittingQuality" input-name="knittingQuality" class="col-span-6" />
    <MergeDropdown
      v-model="selectedMerges"
      input-name="selectedMerges"
      :quality="form.knittingQuality"
      :options="allMerges.map((yarn) => yarn.mergeNumber)"
      class="col-span-6"
      :disabled="!form.knittingQuality"
    />
    <MergeList v-model="form.mergeUsage" form-path="mergeUsage" class="col-span-12" />
    <RollsList
      class="col-span-12"
      form-path="rolls"
      :allowAddRolls="true"
      :includeLength="false"
      :errors="props.errors"
      @update-rolls="updatedRolls"
      :standardBatchSize="form.knittingQuality?.standardBatchSize"
    />
    <DateKnittedInput class="col-span-6" v-model="form.dateKnitted" input-name="dateKnitted" />
    <SupplierInput
      v-model="form.knittingSupplier"
      class="col-span-6"
      input-name="knittingSupplier"
    />
    <WeightInput class="col-span-6" v-model="form.weight" input-name="weight" :disabled="true" />
    <MachineNumberInput
      class="col-span-6"
      v-model="form.machineNumber"
      input-name="machineNumber"
    />
    <BayInput class="col-span-6" v-model="form.bay" input-name="bay" />
    <PurchaseOrderReferenceInput
      class="col-span-6"
      v-model="form.purchaseOrderReference"
      input-name="purchaseOrderReference"
    />
    <NotesInput class="col-span-12" input-name="notes" />
  </div>
</template>

<style scoped></style>
